import { useMutation, useQueryClient } from '@tanstack/react-query'
import { templateService } from '../services/templateService'
import { patientService } from '../services/patientService'
import { consultationService } from '../services/consultationService'
import { patientFileService } from '../services/patientFileService'
import { useAuth } from '../contexts/AuthContext'
import { formatPatientName } from '../utils/name'

export interface UseLetterGenerationParams {
  patientId: string
  templateId: string
  consultationId?: string
  referralId?: string
  providerCustomNote?: string
  markAsInstructions?: boolean
  templateName?: string
}

function createSafeFileName(baseName: string): string {
  return baseName
    .replace(/[^a-zA-Z0-9._-]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '')
    .substring(0, 150)
}

function base64ToFile(base64: string, fileName: string, mimeType?: string): File {
  const byteCharacters = atob(base64.trim())
  const byteNumbers = new Array(byteCharacters.length)
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i)
  }
  const byteArray = new Uint8Array(byteNumbers)
  const blob = new Blob([byteArray], {
    type: mimeType || 'application/vnd.oasis.opendocument.text',
  })
  return new File([blob], fileName, {
    type: mimeType || 'application/vnd.oasis.opendocument.text',
  })
}

export function useLetterGeneration(params: UseLetterGenerationParams) {
  const queryClient = useQueryClient()
  const { user } = useAuth()

  const mutation = useMutation({
    mutationFn: async () => {
      const {
        patientId,
        templateId,
        consultationId,
        referralId,
        providerCustomNote,
        templateName,
      } = params

      const customVariables: Record<string, unknown> = {}
      if (providerCustomNote?.trim()) {
        customVariables.providerCustomNote = providerCustomNote.trim()
        customVariables.referral = {
          ...((customVariables.referral as object) || {}),
          providerCustomNote: providerCustomNote.trim(),
        }
      }

      const response = await templateService.generateDocument({
        templateId,
        patientId,
        consultationId,
        referralId,
        customVariables:
          Object.keys(customVariables).length > 0 ? customVariables : undefined,
      })

      const patient = await patientService.getById(patientId)
      const templateCategory = 'document'
      const patientName = patient ? formatPatientName(patient as any) : 'patient'
      const dateStamp = new Date().toISOString().split('T')[0]
      const safeBaseName = createSafeFileName(
        `${patientName}_${dateStamp}_${templateCategory}`
      )
      const fileName = safeBaseName.endsWith('.odt')
        ? safeBaseName
        : `${safeBaseName}.odt`
      const file = base64ToFile(
        response.content,
        fileName,
        response.mimeType
      )

      await patientFileService.upload(patientId, {
        file,
        displayName: fileName,
        description: templateName
          ? `Generated from template: ${templateName}`
          : 'Generated document',
        category: 'medical-record',
        providerCustomNote: providerCustomNote?.trim() || undefined,
      })

      queryClient.invalidateQueries({ queryKey: ['patient-files', patientId] })
      queryClient.invalidateQueries({
        queryKey: ['patient-files', 'patient', patientId],
      })

      try {
        templateService.downloadDocument(
          response.content,
          response.fileName,
          response.mimeType
        )
      } catch (downloadError) {
        console.error('[useLetterGeneration] Download failed:', downloadError)
      }

      let targetConsultationId = consultationId
      if (!targetConsultationId) {
        try {
          const staffId = user?.id || 'system'
          const documentConsultation = await consultationService.create({
            patientId,
            staffId,
            status: 'completed',
            consultationDate: new Date().toISOString().split('T')[0],
            reason: `Document generation: ${templateName || 'Letter'}`,
            notes: `Document generated from template outside of consultation context.`,
          })
          targetConsultationId = documentConsultation.id
        } catch (error) {
          console.error(
            '[useLetterGeneration] Failed to create document-only consultation:',
            error
          )
        }
      }

      if (targetConsultationId) {
        try {
          await consultationService.addArtefact(targetConsultationId, {
            type: 'referral-letter',
            title: templateName || response.fileName,
            description: `Generated from template: ${templateName || 'Letter'}`,
            status: 'issued',
            content: response.content,
            referenceNumber: response.documentId,
            containsPatientInstructions: params.markAsInstructions ?? false,
          })
          queryClient.invalidateQueries({
            queryKey: ['consultation', targetConsultationId],
          })
          queryClient.invalidateQueries({
            queryKey: ['consultations', 'patient', patientId],
          })
          queryClient.invalidateQueries({ queryKey: ['consultations'] })
        } catch (error) {
          console.error(
            '[useLetterGeneration] Failed to create artefact:',
            error
          )
        }
      }

      return response
    },
  })

  return {
    generate: mutation.mutateAsync,
    isGenerating: mutation.isPending,
    error: mutation.error,
  }
}
